import {
  hasNotificationApi,
  hasServiceWorker,
  isMobilePlatform,
} from '@/lib/platform/capabilities'
import { awaitServiceWorkerRegistration } from '@/lib/platform/serviceWorker'
import { APP_NAME } from '@/lib/pwa'
import { getNotificationPrefs, type ContentLevel } from './prefs'

/**
 * Por que a notificação saiu — ou não saiu.
 *
 * Duas famílias, e confundi-las é o erro caro:
 *
 * **Tentou e não deu** — `unsupported` · `permission-denied` · `no-registration`
 * · `failed`. São os casos em que o usuário pediu para ser avisado e o aviso não
 * chegou: quem chama deve oferecer outra superfície (toast in-app).
 *
 * **Nem tentou** — `disabled-by-pref` · `skipped-tab-visible`. O app decidiu não
 * notificar. Continuam sendo motivo tipado (I3), mas não são falha.
 *
 * `skipped-tab-visible` guarda o nome de quando o divisor era `document.hidden`,
 * e desde a Task 19 quer dizer outra coisa: **o app está na frente do usuário e
 * ele está ativo** (foco da janela + atividade recente — ver [[foco]]). Vale para
 * os três produtores, e o outcome é o que faz o toast in-app tomar o lugar da
 * bandeja sem ninguém consultar visibilidade.
 *
 * `permission-denied` **inclui `default`**. A spec de `showNotification()` rejeita
 * sempre que a permissão "is not 'granted'", e com os defaults deste repo
 * (`prefs.ts:179-180`, ambos `false`) esse é o estado corrente, não a exceção. Ler
 * como "não concedida", nunca como "o usuário recusou".
 */
export type NotifyOutcome =
  | 'sent'
  | 'unsupported'
  | 'permission-denied'
  | 'no-registration'
  | 'failed'
  | 'disabled-by-pref'
  | 'skipped-tab-visible'

export interface NotifyResult {
  outcome: NotifyOutcome
  /** Só em `failed`: a mensagem que a plataforma levantou. */
  error?: string
}

export interface SystemNotificationInput {
  title: string
  body: string
  /** Mesmo `tag` substitui em vez de empilhar na bandeja. Ignorado no Safari macOS. */
  tag: string
  /** Destino do clique (path absoluto). Vai em `data.url`; quem lê é o `public/sw.js`. */
  url: string
  /**
   * Enfeite: `false` em Safari macOS/iOS na BCD. A notificação PRECISA ser
   * inteligível sem isto (I2/P3).
   */
  requireInteraction?: boolean
  /**
   * Ícone GRANDE, quando houver um que informe mais que a marca — o avatar de
   * quem mandou, à moda do WhatsApp. Ausente cai na logo do app (`ICON_PATH`).
   * Hoje o tipo `Contact` não tem campo de avatar, então ninguém passa.
   */
  icon?: string
}

/**
 * Ícone GRANDE — só no desktop.
 *
 * No Chrome Android a notificação já traz a logo do app desenhada pelo sistema;
 * mandar `icon` junto mostra a MESMA imagem duas vezes. Declarar `badge`
 * monocromático não suprime a duplicata (testado em bancada, 2026-09-04), e não
 * existe campo da API que desligue só ali — daí a decisão por plataforma, via
 * `isMobilePlatform()`. No desktop o oposto é verdade: sem `icon`, o Chrome
 * exibe o ícone DELE no lugar da nossa marca.
 */
const ICON_PATH = '/icon-192.png'

/**
 * Máscara monocromática do ícone pequeno. **É ela que evita a logo duplicada.**
 *
 * Bancada 2026-09-04, os dois lados do mesmo defeito: mandando só `icon`, o
 * Chrome Android mostrava a MESMA logo colorida duas vezes, porque sem `badge`
 * ele reaproveita a do `icon` como ícone pequeno; tirando o `icon` para
 * resolver, o Chrome DESKTOP passou a exibir o ícone do Chrome no lugar da nossa
 * marca. Os dois campos têm papéis distintos e os dois precisam existir.
 *
 * O Android consome só o canal alfa (silhueta branca sobre transparente), por
 * isso o asset é monocromático e não a logo reduzida. Pela BCD, `badge` só tem
 * efeito no Chrome — nas demais plataformas é ignorado sem erro (P3).
 */
const BADGE_PATH = '/badge-mono.png'

/**
 * Corpo quando o conteúdo não pode aparecer. Diz que há algo e o que fazer —
 * repetir o título ("Nova mensagem") não acrescenta nada a quem já leu o título.
 */
const GENERIC_BODY = 'Abra o app para ver.'

/**
 * Reduz o que vai para a bandeja ao nível que o usuário escolheu.
 *
 * **Reduz, nunca acrescenta.** É o que preserva a R1 nos três níveis sem
 * repetir a heurística: o chamador já entregou um título seguro
 * ([[chatTitle]] — contato sem nome, inclusive o `fallback_name =
 * phone_number` do back, nunca vira telefone), e função que só apaga não tem
 * como fazer um telefone reaparecer. `full` é identidade justamente por isso:
 * o nível mais aberto é o conteúdo que o chamador já sanitizou, não "tudo".
 *
 * ## Por que `critical` também obedece
 *
 * `critical` é inegociável nas prefs por kind ([[prefs]]) — `setKindChannel` é
 * no-op para ele e a leitura reescreve `byKind.critical` — e fura a regra de
 * foco no inbox (`ignorarFoco`, [[notify]]). A pergunta legítima é se ele
 * deveria furar o nível de conteúdo do mesmo jeito.
 *
 * (Ele **não** fura `browserEnabled`: `notifySystem` devolve `disabled-by-pref`
 * para todo kind. Esta doc afirmava o contrário até 2026-09-04 e a afirmação
 * nunca teve implementação — ver o bullet dos furos em [[prefs]].)
 *
 * **A favor de furar:** o aviso crítico existe pra ser triado na hora, e
 * "OmniChannel · Abra o app para ver." não deixa ninguém julgar urgência sem
 * desbloquear o aparelho. Já que o produto decidiu interromper o usuário contra
 * as prefs POR KIND dele, interromper com informação seria mais coerente.
 *
 * **Contra, e é o que vale:** as duas exceções não são a mesma coisa. Furar as
 * prefs por kind decide sobre a ATENÇÃO do usuário — "esta classe você não
 * desliga" —, e essa é uma decisão que o produto pode tomar por ele. Furar o
 * nível de conteúdo decidiria sobre TERCEIROS: quem está perto do aparelho,
 * nunca consentiu com nada e não é o destinatário. O app não tem como saber
 * quem está olhando a tela de bloqueio, e o usuário respondeu essa pergunta ao
 * escolher o nível NESTE dispositivo.
 *
 * E obedecer não custa a escalada: com `browserEnabled` ligado e no nível mais
 * fechado, o aviso crítico continua saindo, continua ignorando as prefs por
 * kind, continua com `requireInteraction`, e o clique continua abrindo o item.
 * O custo é um desbloqueio a mais. O custo do contrário é irreversível — texto
 * lido por quem não devia não volta atrás, e vazar justamente a classe mais
 * alarmante de mensagem é o pior vazamento possível.
 *
 * Consequência prática: o funil **não precisa saber** o que é crítico. Nenhum
 * campo novo em `SystemNotificationInput`, nenhum call site com poder de furar.
 */
function reduzirConteudo(
  input: SystemNotificationInput,
  nivel: ContentLevel,
): { title: string; body: string } {
  if (nivel === 'generic') return { title: APP_NAME, body: GENERIC_BODY }
  if (nivel === 'sender') return { title: input.title, body: GENERIC_BODY }
  return { title: input.title, body: input.body }
}

/**
 * Única porta de saída para notificação do sistema operacional.
 *
 * **`ServiceWorkerRegistration.showNotification()`, nunca `new Notification()`**
 * (P1): o construtor lança `TypeError` incondicional no Chrome Android — é o
 * defeito que esta feature existe pra consertar, e ele estava sendo engolido por
 * um `console.error` e um `catch {}` vazio.
 *
 * Nada aqui é best-effort silencioso: todo caminho devolve `NotifyResult` (I3).
 */
export async function showSystemNotification(
  input: SystemNotificationInput,
): Promise<NotifyResult> {
  if (!hasNotificationApi() || !hasServiceWorker()) {
    return { outcome: 'unsupported' }
  }

  if (Notification.permission !== 'granted') {
    return { outcome: 'permission-denied' }
  }

  // Espera o registro em vez de desistir na hora: entre o carregamento da página
  // e o retorno do `register()` do mount há uma janela em que o singleton está
  // vazio com o worker JÁ ATIVO (numa recarga ele existe desde antes da página).
  // Mensagem que chegasse nesse intervalo virava `no-registration` e o aviso do
  // SO se perdia — medido em bancada, 2026-09-04.
  const registration = await awaitServiceWorkerRegistration()
  if (!registration) {
    return { outcome: 'no-registration' }
  }

  // A redução acontece AQUI, no funil, e não nos chamadores: `useNotification`
  // e `useBrowserNotificationDispatcher` seguem mandando o conteúdo inteiro.
  // Call site nenhum tem como esquecer o que call site nenhum faz.
  const { title, body } = reduzirConteudo(input, getNotificationPrefs().contentLevel)

  try {
    await registration.showNotification(title, {
      body,
      tag: input.tag,
      // Avatar de quem mandou vence sempre; a marca só onde ela não duplica.
      ...(input.icon
        ? { icon: input.icon }
        : isMobilePlatform()
          ? {}
          : { icon: ICON_PATH }),
      badge: BADGE_PATH,
      ...(input.requireInteraction === undefined
        ? {}
        : { requireInteraction: input.requireInteraction }),
      data: { url: input.url },
    })
    return { outcome: 'sent' }
  } catch (error) {
    return {
      outcome: 'failed',
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

/**
 * O motivo da ULTIMA tentativa, para a tela de preferencias mostrar.
 *
 * Existe porque a bancada e um celular, e num celular **nao ha DevTools**: um
 * motivo que so vive no console e, na pratica, um motivo que ninguem le. O
 * operador cujo aviso nao chega precisa ver o porque no proprio app — e isso
 * deixou de ser instrumentacao de depuracao para virar parte do produto no dia
 * em que a notificacao passou a ter sete formas diferentes de nao sair.
 */
export interface NotifyReport extends NotifyResult {
  /**
   * Qual produtor tentou — muda o que se investiga a seguir. São quatro:
   * `chat` ([[../../hooks/useNotification]], mensagem no canal aberto),
   * `canal` ([[../../hooks/useChannelActivityToast]], `channel_activity` app-wide),
   * `inbox` ([[../../hooks/notifications/useBrowserNotifications]]) e
   * `teste` (o botão do painel de diagnóstico). Foi um `origem: teste` parado na
   * linha "Última tentativa" que provou, em bancada, que o produtor de `/conta`
   * não existia.
   */
  origem: string
}

/**
 * `sessionStorage` e nao memoria: a primeira coisa que qualquer um faz depois de
 * mexer numa configuracao de notificacao e RECARREGAR pra ver se agora vai — e
 * era exatamente ai que o diagnostico sumia. Escopo de sessao da aba e o certo:
 * o motivo descreve esta sessao, nao merece sobreviver a fechar o navegador.
 */
const CHAVE_ULTIMO = 'notif_ultimo_motivo_v1'

function lerUltimo(): NotifyReport | null {
  try {
    const cru = sessionStorage.getItem(CHAVE_ULTIMO)
    if (!cru) return null
    const lido = JSON.parse(cru) as NotifyReport
    return typeof lido?.outcome === 'string' ? lido : null
  } catch {
    // Storage indisponivel (aba privada, cota) ou JSON quebrado. Diagnostico e
    // melhoria: nunca pode derrubar o caminho de notificar.
    return null
  }
}

let ultimo: NotifyReport | null = lerUltimo()
const jaAvisado = new Set<NotifyOutcome>()

/** Estado do diagnostico. `null` = nenhuma tentativa nesta sessao. */
export function ultimoNotifyOutcome(): NotifyReport | null {
  return ultimo
}

/** So para teste — o estado de modulo sobrevive entre casos. */
export function resetNotifyReport(): void {
  ultimo = null
  jaAvisado.clear()
  try {
    sessionStorage.removeItem(CHAVE_ULTIMO)
  } catch {
    /* storage indisponivel */
  }
}

/**
 * Registra o motivo: guarda para a tela e avisa no console.
 *
 * `console.warn` e nao `console.log` de proposito — `next.config.ts` remove
 * `log/debug/info` em producao e mantem `warn/error`, e producao e justamente
 * onde o motivo importa.
 *
 * **Uma vez por motivo**, nunca por mensagem: um canal movimentado emitiria uma
 * linha por mensagem recebida e o aviso se afogaria no proprio ruido. Motivo
 * novo volta a avisar, porque ai e outra causa.
 */
export function reportNotifyOutcome(resultado: NotifyResult, origem: string): void {
  ultimo = { ...resultado, origem }
  try {
    sessionStorage.setItem(CHAVE_ULTIMO, JSON.stringify(ultimo))
  } catch {
    /* storage indisponivel — o diagnostico so perde a sobrevida a recarga */
  }
  if (resultado.outcome === 'sent') return
  if (jaAvisado.has(resultado.outcome)) return
  jaAvisado.add(resultado.outcome)
  console.warn(
    `[notificacao] ${origem}: ${resultado.outcome}` +
      (resultado.error ? ` — ${resultado.error}` : ''),
  )
}
